// Phase 8.4 — Lifted Supplies–style Live Driver Map
// ===================================================
// Real interactive map (Leaflet + TomTom) that replicates the original
// Lifted Supplies driver map and improves it with:
//   • TomTom raster tiles (Dark / Light / Voyager / Satellite styles)
//   • TomTom traffic overlay (toggle)
//   • Driver pins coloured by status (active / on_delivery / stale / offline)
//   • Partner / Franchise / Order pins
//   • Manual routing panel (origin + destination → polyline + ETA)
//   • Route overlay for any assigned delivery_order (auto-fetched)
//   • Map control toolbar (style, traffic, radius, routes, raw GPS, refresh)
//   • Driver status panel (active/on_delivery/stale/offline counters)
//   • Auto-refresh every 12s, toggleable
//   • Graceful "TomTom connection error" card if tiles fail
//
// Backed by `/api/maps/*` (Phase 8.4 backend module). Role-aware.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  MapContainer, TileLayer, LayerGroup, Marker, Popup, Polyline,
  Circle, useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Truck, MapPin, RefreshCw, Locate, Route, Wifi, WifiOff,
  Plus, Layers, Activity, Building2, Store, AlertTriangle,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Default Leaflet marker icon paths (CRA bundling fix)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const STYLES = {
  Dark:      { style: "basic", layer: "main",    filter: "invert(1) hue-rotate(180deg) brightness(0.85) saturate(0.7)" },
  Light:     { style: "basic", layer: "main",    filter: "none" },
  Voyager:   { style: "basic", layer: "main",    filter: "saturate(0.85) brightness(0.95) hue-rotate(330deg)" },
  Satellite: { style: "hybrid", layer: "hybrid", filter: "none" },
};

const STATUS_COLOR = {
  active:      "#22c55e",
  on_delivery: "#7DD3FC",
  stale:       "#f59e0b",
  offline:     "#9CA3AF",
};

function driverIcon(status) {
  const color = STATUS_COLOR[status] || STATUS_COLOR.offline;
  const html = `<div style="background:${color};width:14px;height:14px;border-radius:50%;
                 box-shadow:0 0 8px ${color}aa;border:2px solid #fff"></div>`;
  return L.divIcon({ className: "driver-pin", html, iconSize: [14, 14], iconAnchor: [7, 7] });
}
function poiIcon(type) {
  const color = type === "partner" ? "#F0ABFC"
              : type === "franchise" ? "#FCD34D"
              : type === "warehouse" ? "#A5F3FC"
              : "#B8FF67";
  const html = `<div style="background:${color};width:12px;height:12px;border-radius:3px;
                 border:2px solid #000;box-shadow:0 0 6px ${color}aa"></div>`;
  return L.divIcon({ className: "poi-pin", html, iconSize: [12, 12], iconAnchor: [6, 6] });
}
function orderIcon() {
  const html = `<div style="background:#7DD3FC;width:10px;height:10px;border-radius:50%;
                 border:2px solid #fff;box-shadow:0 0 6px #7DD3FCaa"></div>`;
  return L.divIcon({ className: "order-pin", html, iconSize: [10, 10], iconAnchor: [5, 5] });
}

function FitToData({ drivers, locations, userLocation }) {
  const map = useMap();
  useEffect(() => {
    if (userLocation) {
      try { map.setView([userLocation.lat, userLocation.lng], 11); return; }
      catch { /* noop */ }
    }
    const pts = [
      ...drivers.filter((d) => d.last_location?.lat).map((d) => [d.last_location.lat, d.last_location.lng]),
      ...locations.filter((l) => l.lat).map((l) => [l.lat, l.lng]),
    ];
    if (pts.length >= 1) {
      try { map.fitBounds(pts, { padding: [40, 40], maxZoom: 13 }); }
      catch { /* noop */ }
    }
  }, [drivers.length, locations.length, userLocation, map]);
  return null;
}

export default function LiveDriverMap() {
  const { user } = useAuth();
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [styleName, setStyleName] = useState("Dark");
  const [showTraffic, setShowTraffic] = useState(true);
  const [showRoutes, setShowRoutes] = useState(true);
  const [showRadius, setShowRadius] = useState(false);
  const [showRawGps, setShowRawGps] = useState(true);
  const [liveOn, setLiveOn] = useState(true);
  const [route, setRoute] = useState(null);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [routeBusy, setRouteBusy] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/maps/live-driver-snapshot");
      setSnap(data); setErr("");
    } catch (e) {
      if (e?.response?.status !== 401) setErr(apiErrorMessage(e));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    if (liveOn) pollRef.current = setInterval(load, 12_000);
    return () => clearInterval(pollRef.current);
  }, [liveOn]);

  const provider = snap?.provider;
  const tomtom = provider?.configured && provider?.key;
  const styleCfg = STYLES[styleName] || STYLES.Dark;
  const tileUrl = tomtom
    ? provider.tile_url_template
        .replace("{style}", styleCfg.style)
        .replace("{layer}", styleCfg.layer)
        .replace("{key}",   provider.key)
    : null;
  const trafficUrl = (tomtom && showTraffic)
    ? provider.traffic_url_template.replace("{key}", provider.key)
    : null;

  const drivers = snap?.drivers || [];
  const orders  = snap?.orders  || [];
  const locations = snap?.locations || [];
  const counts = snap?.driver_counts || {};

  const driversWithGps = useMemo(
    () => drivers.filter((d) => d.last_location?.lat != null),
    [drivers],
  );

  const requestRoute = async (e) => {
    e?.preventDefault();
    setRouteBusy(true); setErr("");
    try {
      const { data } = await api.post("/maps/route", {
        origin: { address: origin },
        destination: { address: destination },
      });
      setRoute(data);
    } catch (er) {
      setErr(apiErrorMessage(er));
    } finally { setRouteBusy(false); }
  };

  if (loading) return <div className="p-6 text-lifted-dim text-sm">Loading live map…</div>;
  if (!tomtom) {
    return (
      <div className="p-6">
        <div className="glass-panel border border-amber-400/30 p-4">
          <div className="font-bebas tracking-[0.22em] text-xs text-amber-300">MAP PROVIDER REQUIRED</div>
          <p className="text-sm text-lifted-text mt-1">Set TOMTOM_API_KEY in backend env to activate the live map.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full" data-testid="live-driver-map" style={{ minHeight: "calc(100vh - 80px)" }}>
      {/* Toolbar */}
      <div className="absolute top-3 right-3 z-[500] flex flex-col gap-2 items-end">
        <div className="flex gap-1 bg-black/80 backdrop-blur p-1 rounded-lg border border-white/10">
          {Object.keys(STYLES).map((s) => (
            <button key={s} onClick={() => setStyleName(s)} data-testid={`map-style-${s.toLowerCase()}`}
              className={`px-2 py-1 text-[0.6rem] font-bebas tracking-[0.18em] rounded ${
                s === styleName ? "bg-lifted-green text-black" : "text-lifted-text hover:bg-white/10"}`}>
              {s}
            </button>
          ))}
        </div>
        <div className="flex gap-1 bg-black/80 backdrop-blur p-1 rounded-lg border border-white/10">
          <ToolbarToggle on={showTraffic} setOn={setShowTraffic} label="Traffic" testid="map-toggle-traffic" />
          <ToolbarToggle on={showRadius}  setOn={setShowRadius}  label="Radius"  testid="map-toggle-radius" />
          <ToolbarToggle on={showRoutes}  setOn={setShowRoutes}  label="Routes"  testid="map-toggle-routes" />
          <ToolbarToggle on={showRawGps}  setOn={setShowRawGps}  label="Raw GPS" testid="map-toggle-rawgps" />
          <button onClick={load} data-testid="map-refresh"
            className="px-2 py-1 text-[0.6rem] font-bebas tracking-[0.18em] rounded text-lifted-text hover:bg-white/10 inline-flex items-center gap-1">
            <RefreshCw size={11} /> Refresh
          </button>
          <button onClick={() => setLiveOn((v) => !v)} data-testid="map-toggle-live"
            className={`px-2 py-1 text-[0.6rem] font-bebas tracking-[0.18em] rounded inline-flex items-center gap-1 ${
              liveOn ? "bg-lifted-green/20 text-lifted-green" : "text-lifted-dim hover:bg-white/10"}`}>
            {liveOn ? <Wifi size={11} /> : <WifiOff size={11} />} {liveOn ? "Live" : "Off"}
          </button>
          <button
            onClick={() => {
              if (!navigator.geolocation) {
                setErr("Geolocation is not supported by this browser.");
                return;
              }
              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                  setErr("");
                },
                (e) => setErr(`Location denied: ${e.message}`),
                { enableHighAccuracy: true, timeout: 8000 },
              );
            }}
            data-testid="map-use-my-location"
            className="px-2 py-1 text-[0.6rem] font-bebas tracking-[0.18em] rounded text-lifted-text hover:bg-white/10 inline-flex items-center gap-1"
          >
            <Activity size={11} /> Use my location
          </button>
        </div>
      </div>

      {/* Status badge */}
      <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] bg-black/80 backdrop-blur px-3 py-1 rounded-full border border-white/10 text-[0.65rem] font-bebas tracking-[0.18em] text-lifted-text" data-testid="map-status-badge">
        <span className="text-lifted-green">PROVIDER: TOMTOM</span> ·
        {" "}{locations.length} loc · {drivers.length} drivers · {orders.length} orders
      </div>

      {/* Driver Status Panel */}
      <div className="absolute top-16 left-3 z-[500] bg-black/85 backdrop-blur border border-white/10 rounded-lg p-3 w-64" data-testid="driver-status-panel">
        <div className="font-bebas tracking-[0.22em] text-xs text-lifted-green mb-2 inline-flex items-center gap-1">
          <Activity size={12} /> DRIVER LIVE MAP
        </div>
        <div className="grid grid-cols-2 gap-2">
          <StatBox label="Active"      value={counts.active || 0}      color="#22c55e" />
          <StatBox label="On Delivery" value={counts.on_delivery || 0} color="#7DD3FC" />
          <StatBox label="Stale GPS"   value={counts.stale || 0}       color="#f59e0b" />
          <StatBox label="Offline"     value={counts.offline || 0}     color="#9CA3AF" />
        </div>
        <div className="text-[0.6rem] text-lifted-dim mt-2">Updated just now</div>
        {driversWithGps.length === 0 && (
          <div className="mt-2 text-[0.65rem] text-amber-300 inline-flex items-start gap-1">
            <AlertTriangle size={11} className="shrink-0 mt-0.5" />
            <span>No drivers have GPS yet. Pins appear here as drivers go on-shift.</span>
          </div>
        )}
        <ul className="mt-3 space-y-1 max-h-44 overflow-y-auto pr-1">
          {drivers.slice(0, 30).map((d) => (
            <li key={d.id} className="text-[0.7rem] flex items-center justify-between gap-2">
              <span className="truncate flex items-center gap-1">
                <span className="h-2 w-2 rounded-full" style={{ background: STATUS_COLOR[d.status_live] }} />
                {d.name || d.email || d.id}
              </span>
              <span className="text-[0.55rem] text-lifted-dim uppercase">{d.status_live}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Manual Routing Panel */}
      <div className="absolute bottom-4 left-3 z-[500] bg-black/85 backdrop-blur border border-white/10 rounded-lg p-3 w-72" data-testid="manual-routing-panel">
        <div className="font-bebas tracking-[0.2em] text-[0.7rem] text-lifted-green inline-flex items-center gap-1 mb-2">
          <Route size={12} /> DIRECTIONS (TOMTOM)
        </div>
        <form onSubmit={requestRoute} className="space-y-2">
          <input value={origin} onChange={(e) => setOrigin(e.target.value)}
            placeholder="Origin address…" data-testid="map-route-origin"
            className="w-full bg-black/60 border border-white/10 rounded px-2 py-1.5 text-xs text-white placeholder-lifted-dim focus:border-lifted-green/40 focus:outline-none" />
          <input value={destination} onChange={(e) => setDestination(e.target.value)}
            placeholder="Destination address…" data-testid="map-route-destination"
            className="w-full bg-black/60 border border-white/10 rounded px-2 py-1.5 text-xs text-white placeholder-lifted-dim focus:border-lifted-green/40 focus:outline-none" />
          <button type="submit" disabled={routeBusy || !origin || !destination} data-testid="map-route-submit"
            className="w-full px-2 py-1.5 text-[0.65rem] font-bebas tracking-[0.18em] rounded bg-lifted-green text-black hover:bg-lifted-green/90 disabled:opacity-50 disabled:cursor-not-allowed">
            {routeBusy ? "Routing…" : "Get Directions"}
          </button>
          {route && (
            <div className="text-[0.65rem] text-lifted-text mt-1" data-testid="map-route-summary">
              <div>Distance: <b>{Math.round((route.distance_meters || 0) / 1609.34)} mi</b></div>
              <div>ETA: <b>{Math.round((route.duration_seconds || 0) / 60)} min</b></div>
              {route.traffic_delay_seconds > 0 && (
                <div className="text-amber-300">+{Math.round(route.traffic_delay_seconds / 60)} min traffic</div>
              )}
            </div>
          )}
          {err && <div className="text-[0.65rem] text-rose-300">{err}</div>}
        </form>
      </div>

      {/* Map */}
      <MapContainer
        center={[39.8283, -98.5795]}
        zoom={4}
        scrollWheelZoom
        style={{ height: "calc(100vh - 80px)", width: "100%", background: "#0a0a0a" }}
        attributionControl
      >
        <FitToData drivers={driversWithGps} locations={locations} userLocation={userLocation} />
        <TileLayer
          url={tileUrl}
          attribution={provider.attribution || "© TomTom"}
          maxZoom={22}
          eventHandlers={{
            tileerror: () => setErr("TomTom tile load error — check key"),
          }}
          className="tomtom-tile-layer"
        />
        {trafficUrl && (
          <TileLayer url={trafficUrl} opacity={0.7} maxZoom={22} zIndex={400} />
        )}

        {/* User location pin */}
        {userLocation && (
          <Marker position={[userLocation.lat, userLocation.lng]}>
            <Popup>
              <div className="text-xs" data-testid="map-user-location-popup">
                <div className="font-bold">Your current location</div>
                <div className="opacity-70 text-[10px]">
                  {userLocation.lat.toFixed(4)}, {userLocation.lng.toFixed(4)}
                </div>
              </div>
            </Popup>
            <Circle center={[userLocation.lat, userLocation.lng]} radius={10 * 1609.34}
                    pathOptions={{ color: "#B8FF67", weight: 1, opacity: 0.5, fillOpacity: 0.04 }} />
          </Marker>
        )}

        {/* POI locations (partner / franchise / warehouse) */}
        <LayerGroup>
          {locations.filter((l) => l.lat).map((l) => {
            // Phase 21.1 — explicit Location Source so admins can tell
            // exactly which kind of pin they're looking at.
            const sourceLabel = ({
              partner_store: "Approved partner store",
              franchise: "Franchise location",
              internal_hq: "Internal Lifted HQ (not public)",
              customer_typed: "Customer-typed delivery address",
              tomtom_autocomplete: "TomTom autocomplete suggestion",
              cached_fallback: "Cached/fallback data",
            })[l.source] || "Approved partner store";
            return (
              <Marker key={l.id} position={[l.lat, l.lng]} icon={poiIcon(l.type)}>
                <Popup>
                  <div className="text-xs" data-testid={`map-loc-popup-${l.id}`}>
                    <div className="font-bold">{l.name}</div>
                    <div className="opacity-70 uppercase text-[10px]">{l.type}</div>
                    {l.address && <div className="mt-1">{l.address}</div>}
                    <div className="mt-2 inline-block px-1.5 py-0.5 rounded bg-lifted-green/15 text-lifted-green text-[10px] uppercase tracking-wider">
                      Source: {sourceLabel}
                    </div>
                    {l.public === false && (
                      <div className="mt-1 text-[10px] text-amber-600">
                        Hidden from public delivery map
                      </div>
                    )}
                  </div>
                </Popup>
                {showRadius && l.radius_miles && (
                  <Circle center={[l.lat, l.lng]} radius={l.radius_miles * 1609.34}
                          pathOptions={{ color: "#B8FF67", weight: 1, opacity: 0.4, fillOpacity: 0.05 }} />
                )}
              </Marker>
            );
          })}
        </LayerGroup>

        {/* Driver pins */}
        {showRawGps && (
          <LayerGroup>
            {driversWithGps.map((d) => (
              <Marker key={d.id}
                position={[d.last_location.lat, d.last_location.lng]}
                icon={driverIcon(d.status_live)}>
                <Popup>
                  <div className="text-xs">
                    <div className="font-bold">{d.name || d.email}</div>
                    <div className="opacity-70 uppercase text-[10px]">{d.status_live}</div>
                    <div className="mt-1">{d.vehicle_plate || "—"}</div>
                    <div className="text-[10px] opacity-60">
                      {d.last_location.lat.toFixed(5)}, {d.last_location.lng.toFixed(5)}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </LayerGroup>
        )}

        {/* Order pins */}
        <LayerGroup>
          {orders.map((o) => {
            const lt = o.live_tracking;
            if (!lt?.lat) return null;
            return (
              <Marker key={o.id} position={[lt.lat, lt.lng]} icon={orderIcon()}>
                <Popup>
                  <div className="text-xs">
                    <div className="font-bold">{o.order_id || o.id}</div>
                    <div className="opacity-70 uppercase text-[10px]">{o.status}</div>
                    {o.dropoff_address && <div className="mt-1">{o.dropoff_address}</div>}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </LayerGroup>

        {/* Manual route */}
        {showRoutes && route?.polyline?.length > 0 && (
          <Polyline
            positions={route.polyline.map((p) => [p.lat, p.lng])}
            pathOptions={{ color: "#B8FF67", weight: 4, opacity: 0.9 }}
          />
        )}
      </MapContainer>

      {/* CSS tweak to apply style filters to the tomtom layer */}
      <style>{`
        .tomtom-tile-layer img.leaflet-tile { filter: ${styleCfg.filter}; }
        .leaflet-container { font-family: inherit; }
        .leaflet-popup-content-wrapper, .leaflet-popup-tip {
          background: rgba(0,0,0,0.92); color: #fff; border: 1px solid rgba(255,255,255,0.1);
        }
        .leaflet-control-zoom a { background: rgba(0,0,0,0.8); color: #fff; border-color: rgba(255,255,255,0.1); }
      `}</style>
    </div>
  );
}

function ToolbarToggle({ on, setOn, label, testid }) {
  return (
    <button onClick={() => setOn((v) => !v)} data-testid={testid}
      className={`px-2 py-1 text-[0.6rem] font-bebas tracking-[0.18em] rounded ${
        on ? "bg-purple-500/30 text-purple-100" : "text-lifted-dim hover:bg-white/10"}`}>
      {label}
    </button>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div className="rounded-md p-2 text-center" style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
      <div className="text-2xl font-anton text-white">{value}</div>
      <div className="text-[0.55rem] uppercase tracking-[0.18em]" style={{ color }}>{label}</div>
    </div>
  );
}
